/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import { PickTextAreaWrap } from '@views/Home/index.style'
import useRootStore from '@store/useRootStore'
import { Schema$UserPickCreate } from '@interface/user-pick'
import { UserPickMatchInfo } from '@views/Home/UserPick/Components/UserPickMatchInfo'
import { UserPickMarketInfo } from '@views/Home/UserPick/Components/UserPickMarketInfo'
import {
  ATTACHMENT_TYPE,
  EXCEPTIONAL_UNIQUE_ID,
  FEED_SUPPLIER,
  FROM_VIEW_TYPE,
  GlobalStoreKey,
  USER_PENALTY_TYPE,
} from '@v2/constants/common'
import * as Utils from '@utils/utils'
import { showToast, TOAST_TYPE } from '@utils/utils'
import { AdvEditor, AdvEditorData } from '@components/AdvEditor'
import { useOnMount } from '@utils/reactExt'
import _ from 'lodash'
import DialogUtils from '@utils/dialog-utils'

export interface UserPickEditorProps {
  fromView: FROM_VIEW_TYPE
}

let isSaving = false
export const UserPickEditor: React.FC<UserPickEditorProps> = observer((props) => {
  const { globalStore, bettingHistoryStore, userPickStore, authStore, matchupStore, penaltyStore } = useRootStore()
  const {
    selectedBettingHistory,
    selectedBettingHistoryFoldData,
    selectedBettingHistoryFold,
  } = bettingHistoryStore

  useOnMount(() => {
    isSaving = false
  })

  if (!selectedBettingHistory[FROM_VIEW_TYPE.USER_PICK] || !selectedBettingHistoryFoldData[FROM_VIEW_TYPE.USER_PICK]
    || !selectedBettingHistoryFold[FROM_VIEW_TYPE.USER_PICK]) {
    return null
  }
  const { fromView } = props
  const { bet } = selectedBettingHistory[FROM_VIEW_TYPE.USER_PICK]
  const { id: clubBetId, bettorBetId } = bet

  const {
    matchId,
    sportId,
    categoryId,
    tournamentId,
    dateOfMatch,
    homeTeamId,
    awayTeamId,
  } = selectedBettingHistoryFold[FROM_VIEW_TYPE.USER_PICK]

  const {
    foldId,
    marketType,
    hitState,
    selection,
    specifiers,
    outcomeRate,
  } = selectedBettingHistoryFoldData[FROM_VIEW_TYPE.USER_PICK]

  const { userInfo } = authStore

  const onClosePopup = () => {
    switch (fromView) {
      case FROM_VIEW_TYPE.ADV_HOME:
        userPickStore.fetchUserPickHomePackage().catch(console.error)
        break
      case FROM_VIEW_TYPE.MATCHUP_FILTER:
      case FROM_VIEW_TYPE.USER_PICK_MATCH_LIST:
        userPickStore.fetchUserPickMatchesPackage().catch(console.error)
        break
      case FROM_VIEW_TYPE.USER_PICK_PICK_MATCH:
        userPickStore.fetchUserPickMatchDetailPackage({ matchId }).catch(console.error)
        break
      case FROM_VIEW_TYPE.MATCHUP_DETAIL_PICK:
        userPickStore.setDetailUserPickMarketFilter(EXCEPTIONAL_UNIQUE_ID.TOTAL_USER_PICK)
        userPickStore.fetchDetailUserPickMatchDetailPackage({ matchId, reset: true }).catch(console.error)
        matchupStore.fetchMatchupDetailPackage({ matchId, sportId }).catch(console.error)
        break
    }
    bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.USER_PICK)
    bettingHistoryStore.setBettingHistoryInitProps({ onlyMajorClub: false, fromViewType: FROM_VIEW_TYPE.USER_PICK })
    globalStore.closeGeneralPopup()
  }

  const onSave = async (editorData: AdvEditorData): Promise<boolean> => {
    if (isSaving) {
      return false
    }

    isSaving = true

    if (Utils.isStartedMatch(dateOfMatch)) {
      Utils.showToast(TOAST_TYPE.INFO, '경기 시작 시간이 초과 되었습니다.')
      isSaving = false
      return false
    }
    const userPickData: Schema$UserPickCreate = {
      userNick: userInfo.nick,
      content: editorData.content,
      type: editorData.attachmentType as ATTACHMENT_TYPE,
      attachment: editorData.attachment,
      matchId,
      dateOfMatch,
      sportId,
      categoryId,
      tournamentId,
      homeTeamId,
      awayTeamId,
      marketType: Number(marketType),
      bettorBetId: bettorBetId,
      clubBetId,
      clubBetFoldId: foldId,
      hitState,
      selection,
      specialBetValue: specifiers,
      outcomeRate,
      feedSupplier: FEED_SUPPLIER.BET_RADAR,
    }

    const penaltyMsg = await penaltyStore.checkUserPenalty(USER_PENALTY_TYPE.USER_PICK)
    if (!_.isEmpty(penaltyMsg)) {
      DialogUtils.showPenaltyInfo(penaltyMsg)

      isSaving = false
      return false
    }

    const result = await userPickStore.createUserPick(userPickData)
    if (!result) {
      isSaving = false
      return false
    }

    matchupStore.updateMatchUserPickWritten(matchId)
    showToast(TOAST_TYPE.INFO, '분석글이 게시 되었습니다.')
    onClosePopup()
    isSaving = false
    return result
  }

  const pickContentMinLengthGlobal = globalStore.findGlobalSetting(GlobalStoreKey.USER_PICK_CONTENT_MIN_LENGTH)
  const minLength = pickContentMinLengthGlobal?.value ? Number(pickContentMinLengthGlobal?.value) : 20

  return (
    <Fragment>
      <UserPickMatchInfo matchInfo={selectedBettingHistoryFold[FROM_VIEW_TYPE.USER_PICK]} large/>

      <PickTextAreaWrap>
        <UserPickMarketInfo data={selectedBettingHistoryFoldData[FROM_VIEW_TYPE.USER_PICK]}/>
        <AdvEditor
          showBettingShare={false}
          isFocus={true}
          placeholder={`${minLength}자 이상 입력해주세요.`}
          callbackClose={onClosePopup}
          callbackSave={onSave}
          minLength={minLength}
          maxLine={Number.MAX_SAFE_INTEGER}
          fromViewType={FROM_VIEW_TYPE.USER_PICK}
        />
      </PickTextAreaWrap>
    </Fragment>
  )
})
